import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="mb-3 font-display text-xl font-bold text-primary">{title}</h2>
    <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">{children}</div>
  </section>
);

export default function Confidentialite() {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-primary px-6 py-6 text-primary-foreground">
        <div className="mx-auto flex max-w-3xl items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent"><Shield className="h-5 w-5" /></span>
            <div>
              <h1 className="font-display text-xl font-bold">Politique de confidentialité</h1>
              <p className="text-sm text-primary-foreground/70">Protection de vos données personnelles (RGPD)</p>
            </div>
          </div>
          <Button asChild variant="secondary" size="sm" className="rounded-full">
            <Link to="/" data-testid="privacy-back-link"><ArrowLeft className="mr-1.5 h-4 w-4" /> Accueil</Link>
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-12" data-testid="privacy-content">
        <p className="mb-10 text-sm text-muted-foreground">
          Euroconsult SNC (« Solaria ») s'engage à protéger votre vie privée et à traiter vos données personnelles
          conformément au Règlement Général sur la Protection des Données (RGPD – Règlement UE 2016/679) et à la
          législation belge en vigueur. Dernière mise à jour : {new Date().toLocaleDateString("fr-BE", { month: "long", year: "numeric" })}.
        </p>

        <Section title="1. Responsable du traitement">
          <p>
            Le responsable du traitement de vos données est :<br />
            <strong className="text-foreground">Euroconsult SNC</strong><br />
            Rue des Chanterelles 13, 1470 Genappe, Belgique<br />
            TVA : BE 0644.763.354<br />
            E-mail : <a href="mailto:euroconsult.eu@gmail.com" className="text-accent underline">euroconsult.eu@gmail.com</a><br />
            Téléphone : <a href="tel:+32472195245" className="text-accent underline">0472 19 52 45</a>
          </p>
        </Section>

        <Section title="2. Données collectées">
          <p>Nous collectons uniquement les données que vous nous fournissez volontairement :</p>
          <ul className="list-disc space-y-1 pl-5">
            <li><strong className="text-foreground">Prise de rendez-vous</strong> : nom, adresse e-mail, date/heure souhaitée et message éventuel.</li>
            <li><strong className="text-foreground">Comparateur d'énergie</strong> : données issues de la facture que vous importez (fournisseur, consommation, acomptes, code EAN, adresse). Le document est analysé pour calculer votre comparatif.</li>
            <li><strong className="text-foreground">Contrat</strong> : nom, adresse, code EAN et informations nécessaires à l'établissement du document.</li>
          </ul>
        </Section>

        <Section title="3. Finalités et base légale">
          <ul className="list-disc space-y-1 pl-5">
            <li>Traiter votre demande de rendez-vous ou de devis (base légale : votre consentement et/ou l'exécution de mesures précontractuelles).</li>
            <li>Réaliser votre comparatif énergétique personnalisé (base légale : votre consentement).</li>
            <li>Vous recontacter dans le cadre de votre demande commerciale.</li>
          </ul>
          <p>Vos données ne sont <strong className="text-foreground">jamais vendues</strong> ni cédées à des tiers à des fins commerciales.</p>
        </Section>

        <Section title="4. Durée de conservation">
          <p>
            Vos données sont conservées pendant une durée maximale de <strong className="text-foreground">3 ans</strong> à
            compter de notre dernier contact, sauf obligation légale de conservation plus longue (par exemple obligations
            comptables). Passé ce délai, elles sont supprimées ou anonymisées.
          </p>
        </Section>

        <Section title="5. Vos droits">
          <p>Conformément au RGPD, vous disposez à tout moment des droits suivants :</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>Droit d'accès à vos données ;</li>
            <li>Droit de rectification ;</li>
            <li>Droit à l'effacement (« droit à l'oubli ») ;</li>
            <li>Droit à la limitation et à l'opposition au traitement ;</li>
            <li>Droit à la portabilité de vos données ;</li>
            <li>Droit de retirer votre consentement à tout moment.</li>
          </ul>
          <p>
            Pour exercer ces droits, contactez-nous à{" "}
            <a href="mailto:euroconsult.eu@gmail.com" className="text-accent underline">euroconsult.eu@gmail.com</a>.
            Vous avez également le droit d'introduire une réclamation auprès de l'Autorité de protection des données belge
            (APD) : <a href="https://www.autoriteprotectiondonnees.be" target="_blank" rel="noreferrer" className="text-accent underline">www.autoriteprotectiondonnees.be</a>.
          </p>
        </Section>

        <Section title="6. Sécurité">
          <p>
            Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données contre
            tout accès, perte, altération ou divulgation non autorisés. L'accès aux rendez-vous et données clients est
            protégé par une authentification et réservé au personnel autorisé.
          </p>
        </Section>

        <Section title="7. Cookies">
          <p>
            Ce site n'utilise pas de cookies publicitaires ni de traceurs tiers. Seul un stockage technique strictement
            nécessaire au fonctionnement de l'espace professionnel (connexion sécurisée) peut être utilisé.
          </p>
        </Section>

        <Section title="8. Modifications">
          <p>
            Cette politique peut être mise à jour. Toute modification importante sera publiée sur cette page.
          </p>
        </Section>

        <div className="mt-12 flex items-center gap-2 border-t border-border pt-6 text-sm text-muted-foreground">
          <Sun className="h-4 w-4 text-accent" /> Solaria par Euroconsult — Installation photovoltaïque en Wallonie et en Flandre.
        </div>
      </main>
    </div>
  );
}
